import { InternalMessagesForm } from "features/CenterContent/RoleContent/InternalMessage/schema/formSchema";
import { EvaluationForm } from "features/CenterContent/RoleContent/InternalMessage/shared/EvaluationForm";
import { InternalMessage } from "features/CenterContent/RoleContent/InternalMessage/shared/InternalMessage";
import {
  ComponentProps,
  FormEvent,
  ReactElement,
  useEffect,
  useRef,
} from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollableDialogBody } from "shared/components/ScrollableBody";
import { SegmentedPanel } from "shared/components/SegmentedPanel";
import {
  Box,
  ButtonGroup,
  DialogFooter,
  DialogRoot,
  DropsBadge,
  DropsButton,
  DropsDialogContent,
  DropsDialogHeader,
  DropsStaticAlert,
  DropsTooltip,
  Flex,
  Stack,
  Text,
} from "shared/components/Spor";
import { useConfirmClose } from "shared/form/useConfirmClose";
import { useIgnoreInitialInteractOutside } from "shared/hooks/useIgnoreInitialInteractOutside";
import { FCWC } from "shared/utils/FCWC";
import { useDropsRole } from "stores/useDropsRole";
import { css, RuleSet, useTheme } from "styled-components";

export const delaycloseDuration = 1000;

type ActionDialogProps = {
  title: string;
  subtitle?: string;
  actionTitle: string;
  titleBadge?: string;
  cancelTitle?: string;
  noScrollBar?: boolean;
  onClose: () => void;
  onBack?: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  // ↓ If enabled then submit is clickable when disabled, but runs this function instead
  onSubmitDisabled?: (e: FormEvent<HTMLFormElement>) => void;
  isSubmitDisabled?: boolean;
  onSubmitTooltip?: string;
  loading: boolean;
  isSuccess: boolean;
  isError: boolean;
  successMessage: string;
  failureMessage: string;
  internalMessagesFormMethods?: UseFormReturn<InternalMessagesForm>;
  evaluationForm?: EvaluationForm;
  subHeaders?: ReactElement[];
  contentBodyStyle?: RuleSet<object>;
  size?: ComponentProps<typeof DialogRoot>["size"];
  // ↓ Necessary for small dialogs in order for DatePicker to overflow, otherwise it breaks
  noOverflow?: boolean;
};

export const ActionModal: FCWC<ActionDialogProps> = ({
  title,
  subtitle,
  actionTitle,
  titleBadge,
  cancelTitle,
  noScrollBar,
  onClose: onCloseImmediate,
  onBack,
  onSubmit,
  onSubmitDisabled,
  isSubmitDisabled = false,
  onSubmitTooltip,
  loading,
  isSuccess,
  isError,
  successMessage,
  failureMessage,
  children,
  internalMessagesFormMethods,
  evaluationForm,
  subHeaders,
  contentBodyStyle,
  size = "lg",
  noOverflow = false,
}) => {
  const { onClose, ConfirmCloseAlert } = useConfirmClose(onCloseImmediate);
  const onInteractOutside = useIgnoreInitialInteractOutside();

  useEffect(() => {
    if (isSuccess) {
      setTimeout(onCloseImmediate, delaycloseDuration);
    }
  }, [isSuccess]);

  const theme = useTheme();
  const { t } = useTranslation();
  const { isDriftledningen } = useDropsRole();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrollToBottom = () =>
    scrollRef.current?.scrollBy({
      top: 10000,
      behavior: "smooth",
    });
  function isInfoboxVisible() {
    const container = scrollRef.current;
    const infobox = document.getElementById("infoBoxActionDialog");
    if (!container || !infobox) return true;
    const containerRect = container.getBoundingClientRect();
    const infoboxRect = infobox.getBoundingClientRect();

    const isWithinVerticalBounds =
      infoboxRect.top >= containerRect.top &&
      infoboxRect.bottom <= containerRect.bottom + 50; // 50px extra leeway

    return isWithinVerticalBounds;
  }

  return (
    <>
      <ConfirmCloseAlert />
      <DialogRoot
        open
        onOpenChange={onClose}
        size={size}
        placement="center"
        onInteractOutside={onInteractOutside}
      >
        <DropsDialogContent>
          <DropsDialogHeader>
            <Flex flexDirection="column" gap="6px">
              {title}
              {titleBadge && (
                <DropsBadge semantics="alarm" width="fit-content">
                  {titleBadge}
                </DropsBadge>
              )}
              <Text>{subtitle}</Text>
            </Flex>
          </DropsDialogHeader>
          <form
            onSubmit={(e) => {
              if (isSubmitDisabled && onSubmitDisabled) {
                onSubmitDisabled(e);
                e.nativeEvent.preventDefault(); // Prevent page refresh
                return;
              }
              onSubmit(e);
              e.nativeEvent.preventDefault(); // Prevent page refresh
            }}
            id={`${title}-form`}
          >
            <ScrollableDialogBody
              ref={scrollRef}
              $hideScrollbar={noScrollBar}
              $noOverflow={noOverflow}
              position="relative" // Used for absolute position for internal message
              px={0}
              py={0}
              my={0}
              border={subHeaders ? "none" : undefined} // border and DashedLine does not work well together
              bg="transparent" // bg is set in <SegmentedPanel/>
            >
              <SegmentedPanel
                panelStyle={
                  contentBodyStyle ??
                  css`
                    background-color: ${theme.colorBackgroundSecondary};
                    padding: 24px; /* Different padding than the panelStyle in ProductionDeviation */
                    border-radius: 18px;
                  `
                }
                dashedLineVariant="secondary"
                noOverflow={noOverflow}
              >
                {subHeaders?.map((it, index) => (
                  // eslint-disable-next-line react/no-array-index-key -- We dont have anything else to go by
                  <Stack key={index}>{it}</Stack>
                ))}
                {children && <Stack alignItems="center">{children}</Stack>}
              </SegmentedPanel>
            </ScrollableDialogBody>
            {internalMessagesFormMethods && !isDriftledningen() && (
              <InternalMessage
                formMethods={internalMessagesFormMethods}
                position="absolute"
                evaluationForm={evaluationForm}
              />
            )}
          </form>

          <DialogFooter
            justifyContent="space-between"
            width="100%"
            py="24px"
            flexDir="column"
          >
            {isError && (
              <DropsStaticAlert w="100%" variant="error">
                {failureMessage}
              </DropsStaticAlert>
            )}
            {isSuccess && (
              <DropsStaticAlert
                w="100%"
                variant="success"
                data-testid="action-modal-success-msg"
              >
                {successMessage}
              </DropsStaticAlert>
            )}
            <ButtonGroup justifyContent="space-between" w="100%">
              <DropsButton
                type="button"
                variant="tertiary"
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                }}
              >
                {cancelTitle || "Avbryt"}
              </DropsButton>
              <Flex gap="12px">
                {onBack && (
                  <DropsButton
                    type="button"
                    variant="tertiary"
                    onClick={(e) => {
                      e.preventDefault();
                      onBack();
                    }}
                  >
                    ← {t("Tilbake")}
                  </DropsButton>
                )}
                <DropsTooltip label={onSubmitTooltip}>
                  <Box>
                    <DropsButton
                      aria-invalid={isSubmitDisabled}
                      form={`${title}-form`}
                      disabled={
                        isSuccess || (isSubmitDisabled && !onSubmitDisabled)
                      }
                      variant="primary"
                      onClick={(e) => {
                        if (isInfoboxVisible()) return;
                        scrollToBottom();
                        e.preventDefault();
                      }}
                      type="submit"
                      loading={loading}
                      data-testid="modal_submitButton"
                    >
                      {isError ? t("Prøv igjen") : actionTitle}
                    </DropsButton>
                  </Box>
                </DropsTooltip>
              </Flex>
            </ButtonGroup>
          </DialogFooter>
        </DropsDialogContent>
      </DialogRoot>
    </>
  );
};
